import { useState, useEffect, RefObject } from 'react';

interface Position {
    x: number;
    y: number;
}

interface DragOffset {
    x: number;
    y: number;
}

const ASSISTIVE_TOUCH_SIZE = 40;

export const useDragAndSnap = <T extends HTMLElement>(element: RefObject<T>) => {
    const [position, setPosition] = useState<Position>({
        x: window.innerWidth - ASSISTIVE_TOUCH_SIZE,
        y: window.innerHeight - 30 - ASSISTIVE_TOUCH_SIZE
    });
    const [isDragging, setIsDragging] = useState(false);
    const [dragOffset, setDragOffset] = useState<DragOffset>({ x: 0, y: 0 });

    const handleMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
        setIsDragging(true);
        setDragOffset({
            x: e.clientX - position.x,
            y: e.clientY - position.y
        });
    };

    const handleTouchStart = (e: React.TouchEvent<HTMLDivElement>) => {
        e.preventDefault();
        setIsDragging(true);
        const touch = e.touches[0];
        setDragOffset({
            x: touch.clientX - position.x,
            y: touch.clientY - position.y
        });
    };

    const snapToEdge = () => {
        const windowWidth = window.innerWidth;
        const windowHeight = window.innerHeight;
        const elementWidth = element.current?.offsetWidth || 40;
        const elementHeight = element.current?.offsetHeight || 40;

        const distanceToLeft = position.x;
        const distanceToRight = windowWidth - (position.x + elementWidth);
        const distanceToTop = position.y;
        const distanceToBottom = windowHeight - (position.y + elementHeight);

        const minDistance = Math.min(
            distanceToLeft,
            distanceToRight,
            distanceToTop,
            distanceToBottom
        );

        let newX = position.x;
        let newY = position.y;

        if (minDistance === distanceToLeft) {
            newX = 0;
        } else if (minDistance === distanceToRight) {
            newX = windowWidth - elementWidth;
        } else if (minDistance === distanceToTop) {
            newY = 0;
        } else if (minDistance === distanceToBottom) {
            newY = windowHeight - elementHeight;
        }

        setPosition({ x: newX, y: newY });
    };

    useEffect(() => {
        const handleGlobalMouseMove = (e: MouseEvent) => {
            if (!isDragging) return;

            setPosition({
                x: e.clientX - dragOffset.x,
                y: e.clientY - dragOffset.y
            });
        };

        const handleGlobalTouchMove = (e: TouchEvent) => {
            if (!isDragging) return;
            e.preventDefault();

            const touch = e.touches[0];
            setPosition({
                x: touch.clientX - dragOffset.x,
                y: touch.clientY - dragOffset.y
            });
        };

        const handleGlobalMouseUp = () => {
            if (isDragging) {
                setIsDragging(false);
                snapToEdge();
            }
        };

        const handleGlobalTouchEnd = () => {
            if (isDragging) {
                setIsDragging(false);
                snapToEdge();
            }
        };

        const handleResize = () => {
            const windowWidth = window.innerWidth;
            const windowHeight = window.innerHeight;
            const elementWidth = element.current?.offsetWidth || 40;
            const elementHeight = element.current?.offsetHeight || 40;

            let newX = position.x;
            let newY = position.y;

            if (newX + elementWidth > windowWidth) {
                newX = windowWidth - elementWidth;
            }

            if (newY + elementHeight > windowHeight) {
                newY = windowHeight - elementHeight;
            }

            setPosition({ x: newX, y: newY });
        };

        window.addEventListener('mousemove', handleGlobalMouseMove);
        window.addEventListener('touchmove', handleGlobalTouchMove, { passive: false });
        window.addEventListener('mouseup', handleGlobalMouseUp);
        window.addEventListener('touchend', handleGlobalTouchEnd);
        window.addEventListener('resize', handleResize);

        return () => {
            window.removeEventListener('mousemove', handleGlobalMouseMove);
            window.removeEventListener('touchmove', handleGlobalTouchMove);
            window.removeEventListener('mouseup', handleGlobalMouseUp);
            window.removeEventListener('touchend', handleGlobalTouchEnd);
            window.removeEventListener('resize', handleResize);
        };
    }, [isDragging, dragOffset, position, element]);

    return {
        position,
        isDragging,
        handleMouseDown,
        handleTouchStart
    };
};